import { useMutation, useQueryClient } from "@tanstack/react-query";
import services from "../services";
import { ORDER } from "../enums";
import useNotification from "../../../utils/useNotification";
import { PartConfirmationBucketEntry } from "../../../components/SupplierInventorySearch/utils/partsCalculationUtils";

export type PartConfirmationSendStatus =
  | "sent"
  | "created_without_notify"
  | "failed";

export interface PartConfirmationSendResult {
  supplierId: string;
  supplierName: string;
  status: PartConfirmationSendStatus;
  error?: string;
}

interface SendPartConfirmationBucketInput {
  orderId: string;
  orderNo: string;
  entries: PartConfirmationBucketEntry[];
}

const extractErrorMessage = (error: unknown): string => {
  const anyError = error as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return (
    anyError?.response?.data?.message ||
    anyError?.message ||
    "Failed to send part confirmation"
  );
};

/**
 * Sends a part-confirmation request to every supplier in the Part Confirmation Bucket in
 * one action. Each supplier is an isolated `POST /v1/part-confirmations` — one failure
 * never blocks the rest (`Promise.allSettled`). The payload per supplier is identical to
 * the single-supplier `RequestPartConfirmationModal` flow.
 */
export const useSendPartConfirmationBucket = () => {
  const queryClient = useQueryClient();
  const notify = useNotification();

  return useMutation({
    mutationFn: async ({
      orderId,
      orderNo,
      entries,
    }: SendPartConfirmationBucketInput): Promise<
      PartConfirmationSendResult[]
    > => {
      const settled = await Promise.allSettled(
        entries.map((entry) =>
          services.createPartConfirmation({
            orderId,
            orderNo,
            supplierId: entry.supplierId,
            parts: entry.confirmationParts,
          }),
        ),
      );

      return settled.map((outcome, index) => {
        const entry = entries[index];
        if (outcome.status === "fulfilled") {
          const notified = outcome.value?.notified !== false;
          return {
            supplierId: entry.supplierId,
            supplierName: entry.supplierName,
            status: notified ? "sent" : "created_without_notify",
          };
        }
        return {
          supplierId: entry.supplierId,
          supplierName: entry.supplierName,
          status: "failed",
          error: extractErrorMessage(outcome.reason),
        };
      });
    },
    onSuccess: (results, variables) => {
      const sentCount = results.filter((r) => r.status === "sent").length;
      const createdWithoutNotifyCount = results.filter(
        (r) => r.status === "created_without_notify",
      ).length;
      const failedCount = results.filter((r) => r.status === "failed").length;
      const createdCount = sentCount + createdWithoutNotifyCount;

      queryClient.invalidateQueries({
        queryKey: [ORDER.PART_CONFIRMATION_RESPONSES],
      });
      queryClient.invalidateQueries({
        queryKey: [ORDER.PART_CONFIRMATIONS_FOR_ORDER],
      });
      queryClient.invalidateQueries({
        queryKey: [ORDER.SUPPLIER_INVENTORY_DELTAS],
      });

      if (failedCount === 0 && createdWithoutNotifyCount === 0) {
        notify({
          status: "success",
          title: "Part confirmation sent",
          description: `Sent to ${sentCount} supplier${
            sentCount === 1 ? "" : "s"
          } for enquiry ${variables.orderNo}`,
          duration: 4000,
        });
        return;
      }

      if (failedCount === 0 && createdWithoutNotifyCount > 0) {
        notify({
          status: "warning",
          title: "Part confirmation created",
          description:
            sentCount > 0
              ? `${sentCount} supplier${
                  sentCount === 1 ? " was" : "s were"
                } notified; ${createdWithoutNotifyCount} ${
                  createdWithoutNotifyCount === 1 ? "was" : "were"
                } not notified because business hours have ended for enquiry ${
                  variables.orderNo
                }`
              : `The supplier${
                  createdWithoutNotifyCount === 1 ? "" : "s"
                } ${
                  createdWithoutNotifyCount === 1
                    ? "was"
                    : `(${createdWithoutNotifyCount}) were`
                } not notified because business hours have ended for enquiry ${
                  variables.orderNo
                }`,
          duration: 6000,
        });
        return;
      }

      const failureDetails = results
        .filter((r) => r.status === "failed")
        .map((r) => `${r.supplierName}: ${r.error}`);
      const nonNotifiedDetails = results
        .filter((r) => r.status === "created_without_notify")
        .map(
          (r) =>
            `${r.supplierName}: not notified because business hours have ended`,
        );

      notify({
        status: createdCount > 0 ? "warning" : "error",
        title: `${createdCount} created, ${failedCount} failed`,
        description: [...failureDetails, ...nonNotifiedDetails].join(" | "),
        duration: 6000,
      });
    },
    onError: (error) => {
      notify({
        status: "error",
        title: "Failed to send part confirmation",
        description: extractErrorMessage(error),
      });
    },
  });
};
